// The registration journals: the list on the left, one journal's entries on
// the right — searched, by year, exported or printed. Clerks register and
// correct cards; managers keep the journals and cancel entries (a cancelled
// number stays in the journal and is never given again).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ban, Download, Loader2, Pencil, Plus, Power, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/modules/console/components/ConfirmDialog.jsx';
import { apiErrorMessage, parseApiError } from '@/shared/utils/apiError.js';
import { downloadBlob, fetchAuthedBlob } from '@/shared/utils/authedFetch.js';
import { records } from '../lib/api.js';
import { fmtDay } from '../lib/journals.js';
import { reasonProblem } from '../lib/validation.js';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { ReasonInput } from './dialogs.jsx';
import { JournalDialog } from './JournalDialog.jsx';
import { RegisterDialog } from './RegisterDialog.jsx';

const ALL = 'all';

function CancelDialog({ entry, onOpenChange, onDone }) {
  const { t } = useTranslation('records');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const problem = reasonProblem(reason);
  const checks = useFieldErrors(problem ? { reason: problem } : {});
  const { reset } = checks;
  useEffect(() => {
    setReason('');
    reset();
  }, [entry, reset]);
  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    const res = await records.cancelRegistration(entry.uuid, reason.trim());
    setBusy(false);
    if (res.success) {
      toast.success(t('entry.cancelled', { number: entry.number }));
      onDone();
      onOpenChange(false);
    } else {
      checks.showServer(res, ['reason']);
    }
  };
  return (
    <Dialog open={Boolean(entry)} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('entry.cancelTitle', { number: entry?.number })}</DialogTitle>
          <DialogDescription>{t('entry.cancelDescription')}</DialogDescription>
        </DialogHeader>
        <ReasonInput
          id="cancel-reason"
          value={reason}
          onChange={(v) => { setReason(v); checks.touch('reason'); }}
          error={checks.errors.reason}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || !reason.trim() || checks.blocked} data-testid="entry-cancel-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('entry.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Entries({ journal, access, onChanged }) {
  const { t } = useTranslation('records');
  const navigate = useNavigate();
  const thisYear = new Date().getFullYear();
  const [q, setQ] = useState('');
  const [year, setYear] = useState(String(thisYear));
  const [status, setStatus] = useState(ALL);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    const res = await records.registrations({
      register_uuid: journal.uuid, q: q.trim() || undefined, year: year === ALL ? undefined : Number(year),
      status: status === ALL ? undefined : status, page, size: 50,
    });
    if (res.success) setData(res.response);
    else toast.error(res.message);
  }, [journal.uuid, q, year, status, page]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const changed = () => {
    load();
    onChanged();
  };
  // The bytes come with the session cookie (no token in the URL).
  const exportAs = async (format) => {
    const query = new URLSearchParams({ format, ...(year === ALL ? {} : { year }) });
    try {
      const blob = await fetchAuthedBlob(`${records.registerExport(journal.uuid)}?${query}`);
      downloadBlob(blob, `${journal.name}${year === ALL ? '' : ` ${year}`}.${format}`);
    } catch (e) {
      toast.error(apiErrorMessage(parseApiError(e.detail ? { response: { status: e.status, data: { detail: e.detail } } } : e)));
    }
  };
  const years = Array.from({ length: 6 }, (_, i) => String(thisYear - i));
  const pages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-9 w-56" maxLength={200} placeholder={t('entry.search')} aria-label={t('entry.search')} value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <Select value={year} onValueChange={(v) => { setYear(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-28" aria-label={t('entry.year')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            <SelectItem value={ALL}>{t('entry.allYears')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-40" aria-label={t('entry.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {[ALL, 'registered', 'cancelled'].map((s) => <SelectItem key={s} value={s}>{t(`entry.statuses.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5"><Download className="h-3.5 w-3.5" /> {t('entry.export')}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => exportAs('xlsx')}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => exportAs('csv')}>CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="gap-1.5"
            onClick={() => navigate(`/records/journals/${journal.uuid}/print${year === ALL ? '' : `?year=${year}`}`)}>
            <Printer className="h-3.5 w-3.5" /> {t('entry.print')}
          </Button>
          {access.can_file && journal.active && (
            <Button size="sm" className="gap-1.5" onClick={() => setRegistering(true)} data-testid="entry-new">
              <Plus className="h-3.5 w-3.5" /> {t('entry.new')}
            </Button>
          )}
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : data.items.length === 0 ? (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="entries-empty">{t('entry.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('entry.number')}</th>
                <th className="px-3 py-2">{t('entry.date')}</th>
                <th className="px-3 py-2">{t('entry.correspondent')}</th>
                <th className="px-3 py-2">{t('entry.subject')}</th>
                <th className="px-3 py-2">{t('entry.executor')}</th>
                <th className="px-3 py-2">{t('entry.case')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => {
                const cancelled = row.status === 'cancelled';
                return (
                  <tr key={row.uuid} className={cn('border-t align-top', cancelled && 'text-muted-foreground')} data-testid={`entry-${row.number}`}>
                    <td className={cn('whitespace-nowrap px-3 py-2 font-mono text-xs', cancelled && 'line-through')}>{row.number}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDay(row.reg_date)}</td>
                    <td className="px-3 py-2">
                      {row.correspondent}
                      {row.their_number && (
                        <span className="block text-xs text-muted-foreground">
                          {t('entry.theirs', { number: row.their_number, date: fmtDay(row.their_date) })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {row.subject}
                      {row.file_name && <span className="block text-xs text-muted-foreground">{row.file_name}</span>}
                      {cancelled && <span className="block text-xs">{t('entry.cancelledBecause', { reason: row.cancel_reason })}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.executor_name}
                      {row.due_date && <span className="block text-muted-foreground">{t('entry.dueBy', { date: fmtDay(row.due_date) })}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{row.case_index}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {cancelled ? (
                        <Badge variant="outline">{t('entry.statuses.cancelled')}</Badge>
                      ) : (
                        <>
                          {access.can_file && (
                            <Button variant="ghost" size="icon" aria-label={t('entry.edit')} onClick={() => setEditing(row)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {access.can_manage && (
                            <Button variant="ghost" size="icon" aria-label={t('entry.cancel')} onClick={() => setCancelling(row)}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</Button>
          <span>{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>›</Button>
        </div>
      )}

      <RegisterDialog open={registering} onOpenChange={setRegistering} defaultRegisterUuid={journal.uuid} onDone={changed} />
      <RegisterDialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)} entry={editing} onDone={changed} />
      <CancelDialog entry={cancelling} onOpenChange={(o) => !o && setCancelling(null)} onDone={changed} />
    </div>
  );
}

export function JournalsView({ access, selectedUuid, onSelect }) {
  const { t } = useTranslation('records');
  const [journals, setJournals] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [doomed, setDoomed] = useState(null);

  const load = useCallback(async () => {
    const res = await records.registers();
    if (res.success) setJournals(res.response);
    else toast.error(res.message);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const current = journals?.find((j) => j.uuid === selectedUuid) || journals?.[0] || null;

  const save = async (body) => {
    const editing = form.initial;
    const res = editing ? await records.updateRegister(editing.uuid, body) : await records.createRegister(body);
    if (res.success) {
      toast.success(t('journal.saved'));
      setForm({ open: false, initial: null });
      await load();
      onSelect(res.response.uuid);
    }
    return res; // the dialog shows a refusal beside its field
  };
  const toggle = async (journal) => {
    const res = await records.updateRegister(journal.uuid, { active: !journal.active });
    if (res.success) load();
    else toast.error(res.message);
  };
  const remove = async () => {
    const res = await records.deleteRegister(doomed.uuid);
    if (res.success) {
      toast.success(t('journal.deleted'));
      onSelect(null);
      load();
    } else {
      toast.error(res.message);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-[14rem_1fr]">
      <aside className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{t('journal.list')}</h2>
          {access.can_manage && (
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2" onClick={() => setForm({ open: true, initial: null })} data-testid="journal-new">
              <Plus className="h-3.5 w-3.5" /> {t('plan.new')}
            </Button>
          )}
        </div>
        {!journals ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : journals.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="journals-empty">{t(access.can_manage ? 'journal.noneManage' : 'journal.none')}</p>
        ) : (
          <ul className="space-y-1">
            {journals.map((j) => (
              <li key={j.uuid}>
                <button
                  type="button"
                  onClick={() => onSelect(j.uuid)}
                  aria-current={current?.uuid === j.uuid ? 'page' : undefined}
                  className={cn('w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted cursor-pointer', current?.uuid === j.uuid && 'bg-muted font-medium', !j.active && 'text-muted-foreground')}
                >
                  {j.name}
                  <span className="block text-xs font-normal text-muted-foreground">
                    {t(`journal.kinds.${j.kind}`)} · {t('journal.entries', { count: j.entries })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      <section>
        {current ? (
          <div className="space-y-4" data-testid="journal-view">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-foreground">{current.name}</h2>
                  {!current.active && <Badge variant="outline">{t('journal.inactive')}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t(`journal.kinds.${current.kind}`)} · <span className="font-mono">{current.pattern}</span> · {t(`journal.resets.${current.reset}`)}
                  {current.active && <> · {t('journal.nextNumber', { number: current.next_number })}</>}
                </p>
                {current.note && <p className="mt-1 text-xs text-muted-foreground">{current.note}</p>}
              </div>
              {access.can_manage && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setForm({ open: true, initial: current })}>
                    <Pencil className="h-3.5 w-3.5" /> {t('actions.edit')}
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => toggle(current)}>
                    <Power className="h-3.5 w-3.5" /> {t(current.active ? 'journal.deactivate' : 'journal.activate')}
                  </Button>
                  {current.total === 0 && (
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDoomed(current)}>
                      <Trash2 className="h-3.5 w-3.5" /> {t('actions.delete')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Entries key={current.uuid} journal={current} access={access} onChanged={load} />
          </div>
        ) : (
          journals && <p className="py-16 text-center text-sm text-muted-foreground">{t('journal.pick')}</p>
        )}
      </section>

      <JournalDialog open={form.open} onOpenChange={(open) => setForm((prev) => ({ ...prev, open }))} initial={form.initial} onSave={save} />
      <ConfirmDialog
        open={Boolean(doomed)}
        onOpenChange={(open) => !open && setDoomed(null)}
        title={t('journal.deleteTitle', { name: doomed?.name })}
        confirmLabel={t('actions.delete')}
        destructive
        onConfirm={remove}
      />
    </div>
  );
}
